import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import type { Navigation } from '@/navigation/types/navigation';
import { FloatingOverlay, FloatingPortal } from '@floating-ui/react';
import { useKeyboardEvent } from '@react-hookz/web';
import { clsx } from 'clsx';
import { memo, useEffect, useRef } from 'react';
import FocusLock, { useFocusScope } from 'react-focus-lock';
import { MenuWrapper } from './menu-wrapper';

interface Props {
  menu: Navigation[];
}

const selector = ({
  resetAll,
  isMobileMenuOpen,
  closeMobileMenu,
  navigationPath,
}: NavigationStore) => ({
  isMobileMenuOpen,
  resetAll,
  closeMobileMenu,
  navigationPath,
});

const MobileMenu = ({ menu }: Props) => {
  const asideRef = useRef<HTMLDivElement | null>(null);

  const {
    isMobileMenuOpen: isOpen,
    closeMobileMenu,
    resetAll,
    navigationPath,
  } = useNavigationStore(selector);

  const { autoFocus } = useFocusScope();

  const closeMenu = () => {
    if (!isOpen) return;
    closeMobileMenu();
    const hamburgerButton = document.querySelector(
      '[data-ui="hamburger-button"]',
    );
    if (hamburgerButton instanceof HTMLElement) {
      setTimeout(() => {
        hamburgerButton.focus();
      }, 500);
    }
  };

  const onAsideAnimationComplete = () => {
    if (isOpen) {
      autoFocus();
    } else {
      resetAll();
    }
  };

  useEffect(() => {
    if (!asideRef.current) return;

    if (isOpen) {
      asideRef.current.classList.remove('invisible');
    }

    const transitionEndEvent = (event: TransitionEvent) => {
      if (event.propertyName === 'transform') {
        onAsideAnimationComplete();
        if (!isOpen) {
          asideRef.current?.classList.add('invisible');
        }
      }
    };

    asideRef.current.addEventListener('transitionend', transitionEndEvent);

    return () => {
      asideRef.current?.removeEventListener(
        'transitionend',
        transitionEndEvent,
      );
    };
  }, [isOpen]);

  useKeyboardEvent('Escape', closeMenu);

  return (
    <FloatingPortal>
      <FloatingOverlay
        lockScroll={isOpen}
        onClick={closeMenu}
        className={clsx(
          '!overflow-hidden fixed inset-0 z-top flex h-dvh w-screen flex-col justify-end',
          isOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        style={{
          position: 'fixed',
          overflow: 'auto',
          inset: '0px',
        }}
      >
        <div
          className={clsx(
            'absolute top-0 left-0 h-full w-full bg-overlay backdrop-blur-[3px]',
            'transition-opacity duration-250 ease-in-out',
            isOpen ? 'opacity-100' : 'opacity-0',
          )}
        />
        <aside
          onClick={(e) => e.stopPropagation()}
          ref={asideRef}
          className="invisible relative flex h-full max-h-full l:w-[546px] m:w-[522px] s:w-[510px] w-[272px] xs:w-[312px] flex-col items-start self-end overflow-hidden bg-main pt-xxs transition-transform duration-400 ease-in-out"
          style={{
            //The style need to be applied via style instead of classname, because the transform in tailwind not working
            transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          }}
          aria-expanded={isOpen}
        >
          <FocusLock
            group="mobile-menu"
            className="flex h-full w-full flex-col overflow-y-auto"
            disabled={!isOpen || navigationPath.length > 0}
            autoFocus={false}
          >
            <MenuWrapper data={menu ?? []} closeMenu={closeMenu} />
          </FocusLock>
        </aside>
      </FloatingOverlay>
    </FloatingPortal>
  );
};

export default memo(MobileMenu);
